// BreakdownCell — displays a financial value with an optional hover tooltip.
// When breakdown data exists (any non-zero slot), the value shows a dotted
// underline and hovering reveals a dark popup with DRD / DCP / DKJ amounts plus total.
// Used on Associate Fee, Journal Entry, Check tables.
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatMoney } from "@/utils/format";

const SLOT_LABELS = ["DRD", "DCP", "DKJ"];

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function BreakdownTooltip({ tip, label, breakdown, formatter }) {
  const filled = breakdown.map(toNum);
  const total = filled.reduce((s, v) => s + v, 0);

  return createPortal(
    <div
      style={{ left: tip.x, top: tip.y }}
      className={`fixed z-[9999] -translate-x-1/2 pointer-events-none ${tip.below ? "translate-y-2" : "-translate-y-full"}`}
    >
      <div
        className={`rounded-lg bg-palette-gray-primary text-white text-xs px-4 py-3 shadow-xl min-w-[180px] ${tip.below ? "mt-0" : "mb-2"}`}
      >
        <span className="block font-semibold text-sm mb-2 text-palette-indigo-hover">
          {label} Breakdown
        </span>
        {SLOT_LABELS.map((slotLabel, i) => (
          <div key={slotLabel} className="flex justify-between gap-6 leading-relaxed">
            <span className="text-white">{slotLabel}</span>
            <span className="font-medium">{formatter(filled[i] ?? 0)}</span>
          </div>
        ))}
        <div className="flex justify-between gap-6 border-t border-palette-gray-badge mt-1.5 pt-1.5">
          <span className="text-white">Total</span>
          <span className="font-bold text-white">{formatter(total)}</span>
        </div>
      </div>
      <div
        className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-l-transparent border-r-transparent ${tip.below ? "-top-1.5 border-b-[6px] border-b-palette-gray-primary" : "-bottom-1 border-t-[6px] border-t-palette-gray-primary"}`}
      />
    </div>,
    document.body,
  );
}

export default function BreakdownCell({
  value,
  breakdown,
  label,
  className = "",
  formatter = formatMoney,
  showBreakdown = true,
}) {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);

  const hasBreakdown =
    showBreakdown && Array.isArray(breakdown) && breakdown.some((v) => toNum(v) !== 0);

  const show = useCallback(() => {
    if (!ref.current || !hasBreakdown) return;
    const rect = ref.current.getBoundingClientRect();
    const below = rect.top < 180;
    setTip({
      x: rect.left + rect.width / 2,
      y: below ? rect.bottom : rect.top,
      below,
    });
  }, [hasBreakdown]);

  const hide = useCallback(() => setTip(null), []);

  return (
    <>
      <span
        ref={ref}
        className={`${hasBreakdown ? "cursor-help underline decoration-dotted decoration-palette-gray-placeholder underline-offset-2" : ""} ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {formatter(value)}
      </span>
      {tip && hasBreakdown && (
        <BreakdownTooltip tip={tip} label={label} breakdown={breakdown} formatter={formatter} />
      )}
    </>
  );
}
